/**
 * Boundary de ultimo recurso para toda la app.
 *
 * Sin esto, un error de render en cualquier pantalla deja al funcionario con
 * una pagina en blanco y sin ninguna pista de que hacer. Envuelve el arbol
 * entero en `main.tsx`, por fuera del router: si el que revienta es el propio
 * router, el fallback igual se dibuja.
 *
 * El copy sigue la convencion de mensajes dicientes (CLAUDE.md, Sprint 21-9):
 * dice que paso, avisa que el reporte ya salio solo — el usuario no tiene que
 * reportar nada — y ofrece una accion concreta y una salida si se repite.
 *
 * `Sentry.ErrorBoundary` manda la excepcion sola. Si Sentry no esta
 * inicializado (dev local sin DSN) el boundary sigue funcionando: atrapa y
 * dibuja el fallback, solo que no reporta a ningun lado.
 */

import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";

const SUPPORT_EMAIL = "soporte@kaptar.com.co";

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold text-slate-900">Esta pantalla no se pudo mostrar</h1>
        <p className="mt-3 text-sm text-slate-600">
          El error ya quedó registrado y el equipo de Kaptar lo va a revisar. Recarga la página para
          volver a intentar; si el problema se repite, escribe a{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-brand-700"
        >
          Recargar la página
        </button>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return <Sentry.ErrorBoundary fallback={<ErrorFallback />}>{children}</Sentry.ErrorBoundary>;
}
